import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SEARCH_ADS, GET_CATEGORIES } from '../graphql/queries';
import Layout from '../components/Layout';
import AdCard from '../components/Ads/AdCard';
import {
  Spinner, Row, Col, Form, Button, Card,
  Badge, InputGroup,
} from 'react-bootstrap';

const CONDITION_OPTIONS = [
  { value: '', label: 'Any Condition' },
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filter state from URL so links & back-button work
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [condition, setCondition] = useState(searchParams.get('condition') || '');

  // The query we actually fire (only changes when user submits)
  const [activeQuery, setActiveQuery] = useState(searchParams.get('q') || '');
  const [activeFilters, setActiveFilters] = useState({
    category: searchParams.get('category') || '',
    city: searchParams.get('city') || '',
    minPrice: searchParams.get('minPrice') ? parseFloat(searchParams.get('minPrice')!) : undefined,
    maxPrice: searchParams.get('maxPrice') ? parseFloat(searchParams.get('maxPrice')!) : undefined,
    condition: searchParams.get('condition') || '',
  });

  const { data, loading, error } = useQuery(SEARCH_ADS, {
    variables: {
      query: activeQuery,
      ...(activeFilters.category && { category: activeFilters.category }),
      ...(activeFilters.city && { city: activeFilters.city }),
      ...(activeFilters.minPrice !== undefined && { minPrice: activeFilters.minPrice }),
      ...(activeFilters.maxPrice !== undefined && { maxPrice: activeFilters.maxPrice }),
      ...(activeFilters.condition && { condition: activeFilters.condition }),
      limit: 40,
    },
    skip: !activeQuery.trim(),
  });

  const { data: categoriesData } = useQuery(GET_CATEGORIES);

  const ads = data?.searchAds || [];
  const categories = categoriesData?.rootCategories || [];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = { q: query };
    if (category) params.category = category;
    if (city) params.city = city;
    if (minPrice) params.minPrice = minPrice;
    if (maxPrice) params.maxPrice = maxPrice;
    if (condition) params.condition = condition;
    setSearchParams(params);
    setActiveQuery(query);
    setActiveFilters({
      category,
      city,
      minPrice: minPrice ? parseFloat(minPrice) : undefined,
      maxPrice: maxPrice ? parseFloat(maxPrice) : undefined,
      condition,
    });
  };

  const clearFilters = () => {
    setCategory(''); setCity(''); setMinPrice(''); setMaxPrice(''); setCondition('');
  };

  const activeFilterCount = [category, city, minPrice, maxPrice, condition].filter(Boolean).length;

  return (
    <Layout>
      <div className="mb-4 pw-section p-3 p-md-4">
        <h1 className="fw-bold pw-title mb-1">Search Results</h1>
        {activeQuery && (
          <p className="pw-muted mb-0">
            {loading ? 'Searching…' : `${ads.length} result${ads.length !== 1 ? 's' : ''} for `}
            {!loading && <strong>"{activeQuery}"</strong>}
          </p>
        )}
      </div>

      {/* Search bar + filters */}
      <Card className="mb-4 pw-card">
        <Card.Body>
          <Form onSubmit={handleSearch} className="pw-form">
            <InputGroup className="mb-3">
              <Form.Control
                size="lg"
                placeholder="Search ads…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                aria-label="Search query"
              />
                <Button type="submit" variant="primary" size="lg" className="pw-btn-primary">
                <i className="bi bi-search me-1" />
                Search
              </Button>
            </InputGroup>

            <Row className="g-2 align-items-end">
              <Col xs={12} sm={6} md={3}>
                <Form.Label className="small pw-muted mb-1">Category</Form.Label>
                <Form.Select size="sm" value={category} onChange={e => setCategory(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Label className="small pw-muted mb-1">City</Form.Label>
                <Form.Control size="sm" placeholder="e.g. Kathmandu" value={city} onChange={e => setCity(e.target.value)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small pw-muted mb-1">Min Price (NPR)</Form.Label>
                <Form.Control size="sm" type="number" min={0} placeholder="0" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small pw-muted mb-1">Max Price (NPR)</Form.Label>
                <Form.Control size="sm" type="number" min={0} placeholder="Any" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Label className="small pw-muted mb-1">Condition</Form.Label>
                <Form.Select size="sm" value={condition} onChange={e => setCondition(e.target.value)}>
                  {CONDITION_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md={1} className="d-flex gap-2">
                <Button type="submit" variant="primary" size="sm" className="flex-fill pw-btn-primary">
                  Apply
                </Button>
                {activeFilterCount > 0 && (
                  <Button variant="outline-secondary" size="sm" onClick={clearFilters} className="pw-btn-soft">
                    Clear
                  </Button>
                )}
              </Col>
            </Row>

            {activeFilterCount > 0 && (
              <div className="mt-2 d-flex flex-wrap gap-1">
                {category && <Badge bg="light" text="dark" className="border">Category: {category}</Badge>}
                {city && <Badge bg="light" text="dark" className="border">City: {city}</Badge>}
                {minPrice && <Badge bg="light" text="dark" className="border">Min: NPR {minPrice}</Badge>}
                {maxPrice && <Badge bg="light" text="dark" className="border">Max: NPR {maxPrice}</Badge>}
                {condition && <Badge bg="light" text="dark" className="border">Condition: {condition}</Badge>}
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>

      {/* Results */}
      {!activeQuery.trim() ? (
        <Card className="text-center py-5 pw-empty-state">
          <Card.Body>
            <i className="bi bi-search display-4 text-muted" />
            <h2 className="h5 fw-semibold mt-3">Type something to search</h2>
            <p className="text-muted">Search by title, brand, model, or description.</p>
          </Card.Body>
        </Card>
      ) : loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : error ? (
        <Card className="text-center py-5 pw-empty-state">
          <Card.Body>
            <h2 className="h5 fw-semibold text-danger">Something went wrong</h2>
            <p className="text-muted">Could not load search results. Please try again.</p>
          </Card.Body>
        </Card>
      ) : ads.length === 0 ? (
        <Card className="text-center py-5 pw-empty-state">
          <Card.Body>
            <i className="bi bi-emoji-frown display-4 text-muted" />
            <h2 className="h5 fw-semibold mt-3">No results found</h2>
            <p className="text-muted">
              Try different keywords or remove some filters.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {ads.map((ad: any) => (
            <Col key={ad.id} xs={12} sm={6} lg={4} xl={3} className="mb-4">
              <AdCard ad={ad} />
            </Col>
          ))}
        </Row>
      )}
    </Layout>
  );
};

export default Search;
